import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Shield,
  Upload,
  XCircle,
} from "lucide-react";
import {
  getClamavStatus,
  testClamavScan,
  type ClamavScanResult,
  type ClamavStatus,
} from "@/services/adminApi";

const EICAR_STRING =
  "X5O!P%@AP[4\\PZX54(P^)7CC)7}$EICAR-STANDARD-ANTIVIRUS-TEST-FILE!$H+H*";

const VERDICT_BADGE: Record<
  ClamavScanResult["verdict"],
  { label: string; className: string }
> = {
  OK: {
    label: "Clean",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  FOUND: {
    label: "Malware detecte",
    className: "bg-red-100 text-red-800 border-red-200",
  },
  ERROR: {
    label: "Erreur scan",
    className: "bg-orange-100 text-orange-800 border-orange-200",
  },
  DISABLED: {
    label: "ClamAV desactive",
    className: "bg-gray-100 text-gray-700 border-gray-200",
  },
  UNREACHABLE: {
    label: "Daemon injoignable",
    className: "bg-red-100 text-red-800 border-red-200",
  },
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Parse la string version ClamAV pour extraire l'âge de la DB de signatures.
 * Format: "ClamAV 1.4.3/27995/Fri May  9 06:28:30 2026"
 *                ^^^^^ ^^^^^ ^^^^^^^^^^^^^^^^^^^^^^^^^
 *                version  rev    timestamp DB (build_time)
 *
 * Retourne `{ageDays, ageColor}` ou null si parse impossible.
 */
function parseSignatureAge(version: string | null) {
  if (!version) return null;
  const parts = version.split("/");
  if (parts.length < 3) return null;
  const dbDate = new Date(parts[2].trim());
  if (isNaN(dbDate.getTime())) return null;
  const ageMs = Date.now() - dbDate.getTime();
  const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
  // freshclam pull toutes les 24h ; alerter > 48h (DB stale, freshclam KO)
  const ageColor =
    ageDays >= 7
      ? "text-red-700"
      : ageDays >= 2
      ? "text-orange-600"
      : "text-green-700";
  return { ageDays, ageColor };
}

export default function SecurityPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ClamavStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [scanResult, setScanResult] = useState<ClamavScanResult | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const s = await getClamavStatus(navigate);
      setStatus(s);
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : String(e));
    } finally {
      setStatusLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const handleScan = async () => {
    if (!file) return;
    setScanLoading(true);
    setScanError(null);
    setScanResult(null);
    try {
      const r = await testClamavScan(navigate, file);
      setScanResult(r);
    } catch (e) {
      setScanError(e instanceof Error ? e.message : String(e));
    } finally {
      setScanLoading(false);
    }
  };

  const handleScanEicar = async () => {
    const blob = new Blob([EICAR_STRING], { type: "text/plain" });
    const eicarFile = new File([blob], "eicar.txt", { type: "text/plain" });
    setFile(eicarFile);
    setScanResult(null);
    setScanError(null);
    setScanLoading(true);
    try {
      const r = await testClamavScan(navigate, eicarFile);
      setScanResult(r);
    } catch (e) {
      setScanError(e instanceof Error ? e.message : String(e));
    } finally {
      setScanLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setScanResult(null);
    setScanError(null);
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Securite</h1>
          </div>
        </header>

        <div className="p-6 space-y-6 max-w-4xl">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="h-4 w-4" /> ClamAV — daemon
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshStatus}
                  disabled={statusLoading}
                >
                  <RefreshCw
                    className={`h-3 w-3 mr-1 ${statusLoading ? "animate-spin" : ""}`}
                  />
                  Rafraichir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {statusError && (
                <div className="px-3 py-2 rounded text-sm bg-red-50 text-red-800 border border-red-200 mb-3">
                  {statusError}
                </div>
              )}
              {!status ? (
                <p className="text-sm text-muted-foreground">Chargement...</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Etat</div>
                    <div className="flex items-center gap-1.5 font-medium">
                      {status.reachable ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-green-700">En ligne</span>
                        </>
                      ) : status.enabled ? (
                        <>
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="text-red-700">Injoignable</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">Desactive</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Cible</div>
                    <div className="font-mono text-xs break-all">
                      {status.host}:{status.port}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Latence ping</div>
                    <div className="font-mono">
                      {status.latency_ms != null
                        ? `${status.latency_ms} ms`
                        : "-"}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-xs text-muted-foreground">Version</div>
                    <div className="font-mono text-xs break-all">
                      {status.version ?? "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Âge signatures DB</div>
                    {(() => {
                      const sigAge = parseSignatureAge(status.version);
                      if (!sigAge) return <div className="text-xs">-</div>;
                      return (
                        <div className={`text-xs font-medium ${sigAge.ageColor}`}>
                          {sigAge.ageDays === 0
                            ? "< 24h"
                            : `${sigAge.ageDays} jour${sigAge.ageDays > 1 ? "s" : ""}`}
                          {sigAge.ageDays >= 2 && " ⚠️ freshclam stale ?"}
                        </div>
                      );
                    })()}
                  </div>
                  {status.error && (
                    <div className="md:col-span-3">
                      <div className="text-xs text-muted-foreground">Erreur</div>
                      <div className="text-xs text-red-700 break-all">
                        {status.error_type ? `${status.error_type}: ` : ""}
                        {status.error}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Upload className="h-4 w-4" /> Tester un upload
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Envoie un fichier au daemon ClamAV pour voir le verdict
                (OK / FOUND / ERROR). Aucune ecriture en base, aucun
                traitement metier — c'est uniquement un appel{" "}
                <code className="text-xs bg-gray-100 px-1 rounded">INSTREAM</code>{" "}
                vers clamd.
              </p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-gray-300 file:bg-white file:text-sm file:font-medium hover:file:bg-gray-50"
                />
                <Button
                  size="sm"
                  onClick={handleScan}
                  disabled={!file || scanLoading}
                >
                  {scanLoading ? "Scan en cours..." : "Scanner"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleScanEicar}
                  disabled={scanLoading}
                  title="Envoie la signature EICAR — un antivirus correctement configure DOIT renvoyer FOUND."
                >
                  Tester avec EICAR
                </Button>
              </div>

              {scanError && (
                <div className="px-3 py-2 rounded text-sm bg-red-50 text-red-800 border border-red-200 mb-3">
                  {scanError}
                </div>
              )}

              {scanResult && (
                <div className="border rounded">
                  <div
                    className={`px-3 py-2 border-b text-sm font-medium ${VERDICT_BADGE[scanResult.verdict].className}`}
                  >
                    Verdict — {VERDICT_BADGE[scanResult.verdict].label}
                  </div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Fichier</div>
                      <div className="font-mono text-xs break-all">
                        {scanResult.filename ?? "-"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Taille</div>
                      <div className="font-mono">
                        {formatBytes(scanResult.size_bytes)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Latence</div>
                      <div className="font-mono">{scanResult.latency_ms} ms</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">
                        MIME envoye
                      </div>
                      <div className="font-mono text-xs">
                        {scanResult.declared_mime ?? "-"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">
                        MIME reel (libmagic)
                      </div>
                      <div className="font-mono text-xs">
                        {scanResult.real_mime ?? "-"}
                      </div>
                    </div>
                    {scanResult.signature && (
                      <div className="md:col-span-3">
                        <div className="text-xs text-muted-foreground">
                          Signature
                        </div>
                        <div className="font-mono text-xs text-red-700 break-all">
                          {scanResult.signature}
                        </div>
                      </div>
                    )}
                    {scanResult.error && (
                      <div className="md:col-span-3">
                        <div className="text-xs text-muted-foreground">Erreur</div>
                        <div className="text-xs text-orange-700 break-all">
                          {scanResult.error}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">A quoi ca sert ?</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p>
                <strong>ClamAV</strong> scanne automatiquement les uploads CV
                (PDF), CSV et enregistrements d'entretien envoyes par les
                recruteurs. Si une menace est detectee, l'upload est{" "}
                <strong>rejete avant</strong> d'atteindre S3, le parser PDF ou
                Whisper.
              </p>
              <p>
                Cette page sert a verifier que le daemon est joignable, que
                les signatures sont a jour, et a tester un fichier suspect
                sans toucher au pipeline de production.
              </p>
              <p className="text-xs text-muted-foreground">
                Politique : <strong>fail-closed</strong> cote back/. Si clamd
                est down, les uploads renvoient 503 jusqu'a ce que le daemon
                redevienne joignable.
              </p>
            </CardContent>
          </Card>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
